/**
 * src/App.tsx
 * -----------
 * Root application component. Defines all client-side routes using
 * React Router v6 with lazy-loaded views (each view chunk loads on demand).
 *
 * Route structure:
 *   /login               → LoginPage (unauthenticated)
 *   /                    → AppShell (authenticated layout wrapper)
 *     /                  → Dashboard
 *     /live-wall         → LiveWall
 *     /conversations     → Conversations
 *     /alerts            → Alerts
 *     /logs              → Logs
 *     /knowledge-base    → KnowledgeBase
 *     /flows             → Flows
 *     /prompts           → Prompts
 *     /retrieval         → Retrieval (RAG config + playground)
 *     /crm               → CRM
 *     /voice-style       → VoiceStyle
 *     /appearance        → Appearance
 *     /llm-config        → LLMConfig
 *
 * Auth guard: RequireAuth checks the Zustand auth store.
 * If token is missing/expired, redirects to /login.
 */
import { lazy, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@shared/constants'

// Lazy-loaded views for code splitting
const LoginPage = lazy(() => import('@/views/Login'))
const Dashboard = lazy(() => import('@/views/Dashboard'))
const LiveWall = lazy(() => import('@/views/LiveWall'))
const Conversations = lazy(() => import('@/views/Conversations'))
const Alerts = lazy(() => import('@/views/Alerts'))
const Logs = lazy(() => import('@/views/Logs'))
const KnowledgeBase = lazy(() => import('@/views/KnowledgeBase'))
const Flows = lazy(() => import('@/views/Flows'))
const Prompts = lazy(() => import('@/views/Prompts'))
const Retrieval = lazy(() => import('@/views/Retrieval'))
const CRM = lazy(() => import('@/views/CRM'))
const VoiceStyle = lazy(() => import('@/views/VoiceStyle'))
const Appearance = lazy(() => import('@/views/Appearance'))
const LLMConfig = lazy(() => import('@/views/LLMConfig'))

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loadUser } = useAuth()
  useEffect(() => {
    void loadUser()
  }, [loadUser])
  if (isLoading) return null
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />
  return <>{children}</>
}

export default function App() {
  return (
    <Routes>
      {/* Public route */}
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />

      {/* Protected routes inside AppShell */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path={ROUTES.LIVE_WALL} element={<LiveWall />} />
        <Route path={ROUTES.CONVERSATIONS} element={<Conversations />} />
        <Route path={ROUTES.ALERTS} element={<Alerts />} />
        <Route path={ROUTES.LOGS} element={<Logs />} />
        <Route path={ROUTES.KNOWLEDGE_BASE} element={<KnowledgeBase />} />
        <Route path={`${ROUTES.FLOWS}/*`} element={<Flows />} />
        <Route path={ROUTES.PROMPTS} element={<Prompts />} />
        <Route path={ROUTES.RETRIEVAL} element={<Retrieval />} />
        <Route path={ROUTES.CRM} element={<CRM />} />
        <Route path={ROUTES.VOICE_STYLE} element={<VoiceStyle />} />
        <Route path={ROUTES.APPEARANCE} element={<Appearance />} />
        <Route path={ROUTES.LLM_CONFIG} element={<LLMConfig />} />

        {/* Catch-all redirect to dashboard */}
        <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      </Route>
    </Routes>
  )
}
